/**
 * Friction Log Service
 *
 * Persists friction entries and admin updates to Supabase.
 * Fails gracefully — all errors are logged but never thrown,
 * so the log works in-memory even if the DB table doesn't exist yet.
 *
 * Required table:
 *   friction_log_entries — one row per submitted friction report
 */

import { supabase } from "@/lib/supabase";
import { getCurrentUserRole } from "@/helpers/auth.helpers";
import type { FrictionEntry } from "@/data/frictionLogTypes";
import { toDbEntry, toUiEntry, type FrictionEntryRow } from "@/mappers/friction-log.mapper";

export const FrictionLogService = {
  /**
   * Load all friction entries on mount, newest first.
   * Returns an empty array on failure.
   */
  async loadEntries(): Promise<FrictionEntry[]> {
    try {
      const role = await getCurrentUserRole().catch(() => null);
      const canRead =
        role === "super_admin" || role === "owner" || role === "admin" || role === "it_staff";
      if (!canRead) return [];

      const { data, error } = await supabase
        .from("friction_log_entries")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.warn("[FrictionLogService.loadEntries] DB error:", error.message);
        return [];
      }
      return ((data ?? []) as FrictionEntryRow[]).map(toUiEntry);
    } catch (err) {
      console.warn("[FrictionLogService.loadEntries] Exception:", err);
      return [];
    }
  },

  /**
   * Persist a newly submitted friction entry.
   */
  async saveEntry(entry: FrictionEntry): Promise<void> {
    try {
      const { error } = await supabase
        .from("friction_log_entries")
        .insert(toDbEntry(entry));

      if (error) console.warn("[FrictionLogService.saveEntry] DB error:", error.message);
    } catch (err) {
      console.warn("[FrictionLogService.saveEntry] Exception:", err);
    }
  },

  /**
   * Persist an admin update (status and/or admin notes) on an existing entry.
   */
  async updateEntry(
    id: string,
    updates: Partial<Pick<FrictionEntry, "status" | "adminNotes">>,
  ): Promise<void> {
    try {
      const dbUpdates: Partial<FrictionEntryRow> = {};
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.adminNotes !== undefined) dbUpdates.admin_notes = updates.adminNotes ?? null;

      const { error } = await supabase
        .from("friction_log_entries")
        .update(dbUpdates)
        .eq("id", id);

      if (error) console.warn("[FrictionLogService.updateEntry] DB error:", error.message);
    } catch (err) {
      console.warn("[FrictionLogService.updateEntry] Exception:", err);
    }
  },
};
